import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  fetchHostedDistrictMapUrlOnce,
  getColoradoCommissionDistrictPdfUrl,
  sanitizeDistrictMapHref,
} from '../utils/districtMapUrls';

export interface DistrictMapLinkProps {
  chamber: string | undefined;
  district: string | number | null | undefined;
  /** Visible label; defaults to district string */
  children?: React.ReactNode;
  className?: string;
  /** When true, prefer signed hosted map from API, then commission PDF */
  useHostedMap?: boolean;
  /** When true, show a small external-link icon after the label */
  showIcon?: boolean;
}

/**
 * Link to district map PDF (hosted copy when available, else Colorado commission PDF).
 */
const DistrictMapLink: React.FC<DistrictMapLinkProps> = ({
  chamber,
  district,
  children,
  className = '',
  useHostedMap = true,
  showIcon = false,
}) => {
  const commissionOnlyHref = useMemo(
    () =>
      !useHostedMap && chamber && district !== undefined && district !== null
        ? getColoradoCommissionDistrictPdfUrl(chamber, district)
        : null,
    [useHostedMap, chamber, district]
  );

  const [resolvedHref, setResolvedHref] = useState<string | null | undefined>(
    () => (!useHostedMap ? commissionOnlyHref : undefined)
  );

  useEffect(() => {
    if (district === undefined || district === null || !chamber) {
      setResolvedHref(null);
      return;
    }
    if (!useHostedMap) {
      setResolvedHref(getColoradoCommissionDistrictPdfUrl(chamber, district));
      return;
    }

    let cancelled = false;
    (async () => {
      const hosted = await fetchHostedDistrictMapUrlOnce(chamber, district);
      if (cancelled) return;
      const fallback = getColoradoCommissionDistrictPdfUrl(chamber, district);
      setResolvedHref(hosted ?? fallback);
    })();

    return () => {
      cancelled = true;
    };
  }, [chamber, district, useHostedMap]);

  const label =
    children !== undefined && children !== null
      ? children
      : district !== undefined && district !== null
        ? String(district)
        : '';

  const linkClasses =
    'inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline';

  if (resolvedHref === undefined && useHostedMap) {
    return (
      <span className={`text-inherit ${className}`.trim()}>{label}</span>
    );
  }

  const safeHref = sanitizeDistrictMapHref(resolvedHref);
  if (!safeHref) {
    return <span className={`text-inherit ${className}`.trim()}>{label}</span>;
  }

  return (
    <a
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      className={`${linkClasses} ${className}`.trim()}
      onClick={(e) => e.stopPropagation()}
    >
      {label}
      {showIcon ? <ExternalLink className="w-3 h-3 shrink-0" aria-hidden /> : null}
    </a>
  );
};

export default DistrictMapLink;
